import { FolderIcon, PencilSquareIcon, PhotoIcon, TagIcon } from '@heroicons/react/24/outline';
import { useEffect } from 'react';
import { ICON_BUTTON_SIZE } from '../components/touch-target';
import { Link, useLocation } from 'react-router-dom';
import { useAppConfig } from '../app-config';
import { useCanWrite } from '../auth';
import { useI18n } from '../i18n';
import { GALLERY_TABS, type GalleryTab } from './state';
import { rememberTabForPath } from './tab-pref';
import { pathToTab, tabToPath } from './url-state';

/**
 * Rangée de navigation PRIMAIRE de l'en-tête (XPHOTO-18) : les TROIS onglets galerie
 * rendus en ICÔNES SEULES (Galerie / Dossiers / Tags), avec tooltip, suivis d'un
 * RACCOURCI « À valider (IA) » (avec badge de compteur).
 *
 * « Galerie » Y FIGURE (XPHOTO-32, lot 1). Elle en était exclue au motif qu'elle
 * ferait doublon avec la Maison « Accueil » du fil d'Ariane, qui pointait alors en dur
 * sur `/`. Ce raisonnement ne tient plus, pour deux raisons : depuis l'onglet Dossiers,
 * aucune affordance VISIBLE ne ramenait à la Galerie (rangée asymétrique, deux onglets
 * sur trois) ; et la Maison ne mène plus à la Galerie mais au DERNIER ONGLET VISITÉ
 * (`tab-pref.ts`) — elle ne peut donc plus tenir lieu d'onglet « Galerie ». Les trois
 * onglets sont désormais explicites et symétriques.
 *
 * C'est ici qu'on MÉMORISE l'onglet courant, sur le CHEMIN EXACT (`rememberTabForPath`)
 * — jamais sur l'onglet dérivé, `pathToTab` faisant retomber `/photo/:id` sur
 * « Galerie ». Écrire une préférence est un effet SANS conséquence sur l'URL : rien
 * n'est redirigé ni réécrit.
 *
 * Le raccourci « À valider (IA) » N'EST PAS un onglet : c'est un LIEN DE FILTRE vers
 * la galerie filtrée `?taguees=avalider` (photos non taguées porteuses de suggestions
 * IA à réviser). Il est ACTIF quand la query courante porte `taguees=avalider`.
 *
 * Ce sont de vrais LIENS de navigation GLOBALE : cliquer « Dossiers » depuis
 * `/admin` navigue vers `/dossiers`. L'onglet actif est dérivé de la ROUTE
 * (`useLocation`), pas d'un état local — la barre est présente sur TOUTES les
 * routes authentifiées (l'en-tête étant fourni par le layout `GalleryView`).
 *
 * A11y : chaque bouton porte son libellé i18n en `aria-label` ET `title` (nom
 * accessible stable, utilisé par les tests) ; l'état actif combine PLUSIEURS
 * indices (fond `bg-brand/10`, soulignement, `aria-current="page"`) — jamais la
 * couleur seule. Le raccourci « À valider (IA) » garde un badge (icône + nombre).
 */

const ICONS: Record<GalleryTab, typeof PhotoIcon> = {
  gallery: PhotoIcon,
  folders: FolderIcon,
  tags: TagIcon,
};

const LABEL_KEY: Record<GalleryTab, 'nav.gallery' | 'nav.folders' | 'nav.tags'> = {
  gallery: 'nav.gallery',
  folders: 'nav.folders',
  tags: 'nav.tags',
};

// NAVIGATION PRIMAIRE : passée de 36 px à 44 px (WCAG 2.5.5). L'en-tête fait `h-14`
// (56 px), la place existe.
const TAB_LINK_CLASS = `relative ${ICON_BUTTON_SIZE} rounded-md transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none`;
const ACTIVE_CLASS =
  'bg-brand/10 text-brand after:absolute after:inset-x-1.5 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand';
const IDLE_CLASS = 'text-muted hover:bg-surface-2 hover:text-fg';

interface GalleryTabsBarProps {
  /** Compteur du raccourci « À valider (IA) » = photos non taguées à suggestions IA. Omis/0 ⇒ pas de badge. */
  aiReviewCount?: number;
}

export function GalleryTabsBar({ aiReviewCount = 0 }: GalleryTabsBarProps): React.JSX.Element {
  const { t, tp } = useI18n();
  const canWrite = useCanWrite();
  const location = useLocation();
  // Coupe-circuit IA global (Incrément B) : sans IA, le raccourci « À valider (IA) » n'a
  // aucun sens → on le masque. `GalleryTabsBar` est rendu SOUS `AppConfigProvider` (via
  // `AppChrome` → `AppShell` → `Header`), donc `useAppConfig` renvoie bien la valeur du
  // contexte (défaut prudent `true` tant que `GET /api/app-config` n'a pas répondu).
  const { aiEnabled } = useAppConfig();
  const active = pathToTab(location.pathname);
  // Mémorisation du dernier onglet visité (cible du lien Maison). Sur le CHEMIN, donc
  // valable aussi bien pour un clic d'onglet que pour le fil d'Ariane ou un lien direct
  // — et NO-OP hors des trois bases d'onglet (`/photo/:id`, `/admin`…). Aucun
  // `navigate()` ici : un effet n'a JAMAIS le droit de réécrire l'URL (cf. `tab-pref`).
  useEffect(() => {
    rememberTabForPath(location.pathname);
  }, [location.pathname]);
  // Raccourci « À valider (IA) » actif quand la galerie est filtrée `?taguees=avalider`.
  const aiReviewActive = new URLSearchParams(location.search).get('taguees') === 'avalider';
  const aiReviewLabel = t('nav.aiReview');

  return (
    <nav aria-label={t('gallery.tabsLabel')} className="flex items-center gap-0.5">
      {GALLERY_TABS.map((tab) => {
        const Icon = ICONS[tab];
        const selected = tab === active;
        const label = t(LABEL_KEY[tab]);
        return (
          <Link
            key={tab}
            to={tabToPath(tab)}
            aria-label={label}
            title={label}
            aria-current={selected ? 'page' : undefined}
            className={`${TAB_LINK_CLASS} ${selected ? ACTIVE_CLASS : IDLE_CLASS}`}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
          </Link>
        );
      })}

      {/* Raccourci « À valider (IA) » : lien de FILTRE (pas un onglet) vers la galerie
          filtrée `?taguees=avalider`, avec badge du nombre de photos à réviser. MASQUÉ
          quand le coupe-circuit IA global est désactivé (`aiEnabled === false`) ET pour
          un LECTEUR (audit A3) : il n'était gardé que par `aiEnabled`, si bien qu'un
          lecteur voyait l'entrée et son badge, filtrait la galerie… et n'y trouvait
          AUCUNE action « Taguer » (toutes masquées par `canWrite`). Une impasse — la
          même que depuis la cloche. Réviser des propositions IA est une écriture. */}
      {aiEnabled && canWrite && (
        <Link
          to="/?taguees=avalider"
          aria-label={aiReviewLabel}
          title={aiReviewLabel}
          aria-current={aiReviewActive ? 'page' : undefined}
          className={`${TAB_LINK_CLASS} ${aiReviewActive ? ACTIVE_CLASS : IDLE_CLASS}`}
        >
          <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
          {aiReviewCount > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-brand px-1 py-px text-[0.625rem] font-semibold leading-none text-brand-fg"
              aria-label={tp('nav.aiReviewCount', aiReviewCount)}
            >
              {aiReviewCount}
            </span>
          )}
        </Link>
      )}
    </nav>
  );
}
